import GolfIcon from '@/components/GolfIcon';
import { Button } from '@/components/ui/button';
import { GolfAvatar } from '@/components/ui/GolfAvatar';
import { IonBackButton, IonHeader, IonToolbar } from '@ionic/react';
import { t } from 'i18next';
import React from 'react';
import { Participant } from '.';

type ChatHeaderProps = {
  chatDetails: {
    id: string;
    name?: string;
    type?: 'direct' | 'group';
    imageUrl?: string;
    participants?: string[];
    online?: boolean;
    lastSeen?: string;
  } | null;
  participants: Participant[];
  userId: string;
  toggleInfoPanel: () => void;
  onBackButton?: () => void;
};

export const ChatHeader: React.FC<ChatHeaderProps> = ({ chatDetails, participants, userId, toggleInfoPanel, onBackButton }) => {
  if (!chatDetails) return null;

  // Get the other participant in a direct chat
  const getOtherParticipant = () => {
    if (chatDetails.type === 'direct' && participants.length > 0) {
      return participants.find(p => p.id !== userId) || participants[0];
    }
    return null;
  };

  // Get display name based on chat type
  const getChatDisplayName = () => {
    if (chatDetails.type === 'group') {
      // For group chats, use the chat name
      return chatDetails.name || 'Group Chat';
    } else {
      // For direct chats, use the other participant's name
      const otherParticipant = getOtherParticipant();
      if (otherParticipant) {
        return `${otherParticipant.firstName} ${otherParticipant.lastName}`;
      }
      return chatDetails.name || 'Chat';
    }
  };

  // Get avatar image based on chat type
  const getChatAvatarUrl = () => {
    if (chatDetails.type === 'group') {
      // For group chats, use the chat image
      return chatDetails.imageUrl;
    } else {
      // For direct chats, use the other participant's image
      const otherParticipant = getOtherParticipant();
      return otherParticipant?.imageUrl || chatDetails.imageUrl;
    }
  };

  // Get status text based on chat type
  const getStatusText = () => {
    if (chatDetails.type === 'group') {
      return `${chatDetails.participants?.length || 0} ${t('members')}`;
    }

    const otherParticipant = getOtherParticipant();
    if (!otherParticipant) return t('offline');

    if (otherParticipant.online) {
      return t('online');
    }

    if (otherParticipant.lastSeen) {
      const lastSeen = new Date(otherParticipant.lastSeen);
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60 * 60));

      if (diffInHours < 1) {
        return t('last_seen_recently');
      } else if (diffInHours < 24) {
        return `${t('last_connected_at')} ${diffInHours} ${t('hour', { count: diffInHours })}`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${t('last_connected_at')} ${diffInDays} ${t('day', { count: diffInDays })}`;
      }
    }

    return t('offline');
  };

  return (
    <IonHeader className="ion-no-border" translucent={true}>
      <IonToolbar>
        <div className="flex items-center gap-3 p-2">
          <IonBackButton defaultHref="/messages" text="" />
          {onBackButton && (
            <div className="absolute inset-0 z-10" onClick={onBackButton}></div>
          )}
          <GolfAvatar className="size-10" imageUrl={getChatAvatarUrl()} />
          <div className="flex flex-1 flex-col">
            <h3 className="font-semibold">{getChatDisplayName()}</h3>
            <p className="text-xs text-muted-foreground">{getStatusText()}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleInfoPanel}>
            <GolfIcon name="information" className="size-5" />
          </Button>
        </div>
      </IonToolbar>
    </IonHeader>
  );
};
